import './Player.scss';
import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom';
import { fetchPlayerTracks, fetchPlayerFreeTracks, Track, Artist } from '../../services/api';
import ImageStorage from '../ImageStorage/ImageStorage';
import { useAuthContext } from '../../context/AppContext';
import { usePlayerContext, enterBrowserFullscreen } from '../../context/PlayerContext';
import { useNavigate } from 'react-router-dom';
import FavoriteButton from '../FavoriteButton/FavoriteButton';
import { useStreamUrl } from '../../hooks/useStreamUrl';

const MarqueeText: React.FC<{ text: string }> = ({ text }) => {
  const spanRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const span = spanRef.current;
    if (!span) return;
    const parent = span.parentElement!;
    const overflow = span.scrollWidth - parent.clientWidth;
    if (overflow > 0) {
      span.style.setProperty('--marquee-offset', `-${overflow}px`);
      span.style.animationName = '';
    } else {
      span.style.removeProperty('--marquee-offset');
      span.style.animationName = 'none';
    }
  }, [text]);

  return <span ref={spanRef} className="marquee-text">{text}</span>;
};

// Transport icons for the phone player (mini player and "Now playing").
const PrevIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 6V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M9.5 12L18 6V18L9.5 12Z" fill="currentColor"/>
  </svg>
);

const NextIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M18 6V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M14.5 12L6 18V6L14.5 12Z" fill="currentColor"/>
  </svg>
);

const PlayPauseIcon = ({ size, isPlaying, loading }: { size: number; isPlaying: boolean; loading: boolean }) =>
  loading ? (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className='player__btn--loading' aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeDasharray="40 20" strokeLinecap="round"/>
    </svg>
  ) : isPlaying ? (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor"/>
      <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor"/>
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M8 5.14286V18.8571L19 12L8 5.14286Z" fill="currentColor"/>
    </svg>
  );

const formatTime = (seconds: number): string => {
  if (isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const Player = () => {
  const navigate = useNavigate();

  const { track, setTrack, playerTrackList, setPlayerTrackList, playlist, setPlaylist, setModalArtist, setIsModalArtistOpen, isFullscreen, setIsFullscreen } = usePlayerContext();
  const { isMember, loading, authReady, user, setIsPricingModalOpen } = useAuthContext();

  const [currentTrackIndex, setCurrentTrackIndex] = useState<number | null>(null);
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  // Signed audio URL from the backend (/tracks/:id/stream), not a public Storage URL.
  const { url: streamUrl, error: streamError } = useStreamUrl(currentTrack?.id, user?.id, currentTrack?.audio_url);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playedIndices, setPlayedIndices] = useState<Set<number>>(new Set());
  const [history, setHistory] = useState<number[]>([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [controlsDisabled, setControlsDisabled] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [isAudioReady, setIsAudioReady] = useState(false);
  const [isShuffleMode, setIsShuffleMode] = useState(true);
  // Phones: the full-screen "Now playing", opened from the mini player (like the app).
  const [isNowPlayingOpen, setIsNowPlayingOpen] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasInitialized = useRef(false);
  const isInitialPlaylistLoad = useRef(true);
  const previousTrackId = useRef<string | null>(null);
  // The full list the random button picks from (all published tracks for members, free ones otherwise).
  const allTracksRef = useRef<Track[]>([]);
  // Set by the random button: the track it already picked for the new playlist. The list and the
  // index change together, so the player never shows the old position in the new list.
  const pendingStartIndexRef = useRef<number | null>(null);
  const isManualTrackChange = useRef(false);
  // Always the latest playNextTrack, so a track ending uses the current shuffle mode and history
  // (the audio listeners are only attached when the track changes).
  const playNextTrackRef = useRef<() => void>(() => {});

  // --------------------------------
  // LOAD TRACKS INITIAL (solo una vez al montar)
  // --------------------------------
  useEffect(() => {
    if (loading || !authReady) return;
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const loadTracks = async () => {
      try {
        const data = isMember ? await fetchPlayerTracks() : await fetchPlayerFreeTracks();
        allTracksRef.current = data;
        setPlayerTrackList(data);
      } catch (err) {
        console.error('Failed to load tracks:', err);
      }
    };

    loadTracks();
  }, [loading, authReady, isMember, setPlayerTrackList]);

  // --------------------------------
  // REACT TO PLAYLIST CHANGES
  // --------------------------------
  useEffect(() => {
    if (playerTrackList.length === 0) return;

    if (isInitialPlaylistLoad.current) {
      isInitialPlaylistLoad.current = false;
      return;
    }
    
    if (pendingStartIndexRef.current !== null) {
      // The random button already set the index along with the list.
      setPlayedIndices(new Set([pendingStartIndexRef.current]));
      pendingStartIndexRef.current = null;
    } else if (isShuffleMode) {
      const randomIndex = Math.floor(Math.random() * playerTrackList.length);
      setCurrentTrackIndex(randomIndex);
      setPlayedIndices(new Set([randomIndex]));
    } else {
      setCurrentTrackIndex(0);
      setPlayedIndices(new Set([0]));
    }
    setHistory([]);
    // intentionally NOT setting isPlaying(false) here —
    // play state is owned by track-change effect and user actions only
    // Only a new playlist restarts playback; toggling shuffle must not jump to another track.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerTrackList]);

  // --------------------------------
  // UPDATE CURRENT TRACK cuando cambie el índice
  // --------------------------------
  useEffect(() => {
    if (currentTrackIndex !== null && playerTrackList.length > 0) {
      const track = playerTrackList[currentTrackIndex];
      setCurrentTrack(track);
      setControlsDisabled(false);
    } else {
      setCurrentTrack(null);
      setControlsDisabled(true);
    }
  }, [currentTrackIndex, playerTrackList]);

  // --------------------------------
  // REACT TO TRACK CHANGES (cuando se selecciona un track específico)
  // --------------------------------
  useEffect(() => {
    if (!track || playerTrackList.length === 0) return;

    const trackIndex = playerTrackList.findIndex(t => t.id === track.id);
    
    if (trackIndex !== -1 && previousTrackId.current !== track.id) {
      previousTrackId.current = track.id;
      isManualTrackChange.current = true;
      
      if (currentTrackIndex !== null && currentTrackIndex !== trackIndex) {
        setHistory(prev => [...prev, currentTrackIndex]);
      }
      
      setCurrentTrackIndex(trackIndex);
      
      if (!playedIndices.has(trackIndex)) {
        setPlayedIndices(prev => new Set([...prev, trackIndex]));
      }
      
      setIsPlaying(true);
    }
  }, [track, playerTrackList, currentTrackIndex, playedIndices]);

  // --------------------------------
  // RESET WHEN TRACK CHANGES — mark audio as not ready until it loads
  // --------------------------------
  useEffect(() => {
    setDuration(0);
    setCurrentTime(0);
    setIsAudioReady(false);
  }, [currentTrack]);

  // --------------------------------
  // AUDIO EVENTS + SYNC PLAYING STATE
  // --------------------------------
  useEffect(() => {
    if (!currentTrack) return;

    let pollAttempts = 0;
    let cleanupFn: (() => void) | null = null;

    const setup = () => {
      const audio = audioRef.current;

      if (!audio) {
        if (pollAttempts < 30) { // 30 × 50ms = 1500ms
          pollAttempts++;
          setTimeout(setup, 50);
        }
        return;
      }

      const markReady = () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          setDuration(audio.duration);
        }
        setIsAudioReady(true);
      };

      const updateDuration = () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          setDuration(audio.duration);
        }
      };

      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
        updateDuration(); // no re-render when the value is unchanged
      };

      const handleLoadedMetadata  = () => updateDuration();
      const handleLoadedData      = () => updateDuration();
      const handleCanPlay         = () => {
        markReady();
        audio.play().catch((err) => {
          console.error('Error auto-playing audio:', err);
          setIsPlaying(false);
        });
      };
      const handleDurationChange  = () => updateDuration();
      const handleEnded           = () => playNextTrackRef.current();
      // Read the element's real state: 'play'/'pause' events arrive late, and trusting the event
      // type made a late 'pause' and a late 'play' keep undoing each other (play/pause loop).
      const handlePlay            = () => { setIsPlaying(!audio.paused); updateDuration(); };
      const handlePause           = () => setIsPlaying(!audio.paused);

      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('loadeddata', handleLoadedData);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('durationchange', handleDurationChange);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);

      setIsPlaying(!audio.paused);

      // Only once the new track's link is set: before that, the element still holds the previous
      // song, which would mark the new one ready and skip the loading icon.
      if (streamUrl && audio.readyState >= 2) {
        markReady();
      }

      cleanupFn = () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('loadeddata', handleLoadedData);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('durationchange', handleDurationChange);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
      };
    };

    setup();

    return () => {
      cleanupFn?.();
    };
    // streamUrl: the <audio> element only exists once the signed URL has arrived.
  }, [currentTrack, streamUrl]);

  // --------------------------------
  // STREAM ERRORS — member tracks need a logged-in member
  // --------------------------------
  useEffect(() => {
    if (!streamError) return;
    setIsPlaying(false);
    if (streamError.status === 401) navigate('/login');
    else if (streamError.status === 403) setIsPricingModalOpen(true);
    else console.error('Error loading audio:', streamError.message);
  }, [streamError, navigate, setIsPricingModalOpen]);

  // --------------------------------
  // ONE SONG AT A TIME
  // --------------------------------
  // While the next track's link loads, the previous song must not keep playing.
  useEffect(() => {
    if (!streamUrl) audioRef.current?.pause();
  }, [streamUrl]);

  // Across tabs too: when this tab starts playing, the site's other tabs pause.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('mattdigging-player');
    const onPlay = () => channel.postMessage('playing');
    channel.onmessage = () => audio.pause();
    audio.addEventListener('play', onPlay);
    return () => {
      audio.removeEventListener('play', onPlay);
      channel.close();
    };
  }, []);

  // --------------------------------
  // PLAY / PAUSE CONTROL
  // --------------------------------
  useEffect(() => {
    const audio = audioRef.current;
    // No link yet: nothing to play (the old song stays paused); 'canplay' starts the new one.
    if (!audio || !currentTrack || !streamUrl) return;

    // Only act when the element is not already in that state, so this never fights the events.
    // Never on a finished song: play() would restart it from 0 while the next track's link loads
    // ('canplay' starts the next track).
    if (isPlaying && audio.paused && !audio.ended) {
      audio.play().catch(() => {
        setIsPlaying(!audio.paused);
      });
    } else if (!isPlaying && !audio.paused) {
      audio.pause();
    }
  }, [isPlaying, currentTrack, streamUrl]);

  // --------------------------------
  // Actualizar el track en el contexto cuando cambie currentTrack
  // --------------------------------
  useEffect(() => {
    if (currentTrack) {
      if (isManualTrackChange.current) {
        isManualTrackChange.current = false;
        return;
      }
      
      if (currentTrack.id !== previousTrackId.current) {
        previousTrackId.current = currentTrack.id;
        setTrack(currentTrack);
      }
    }
  }, [currentTrack, setTrack]);

  // --------------------------------
  // RANDOM LOGIC
  // --------------------------------
  const getRandomUnplayedIndex = (): number | null => {
    if (playerTrackList.length === 0) return null;

    if (playedIndices.size >= playerTrackList.length) {
      setPlayedIndices(new Set());
      return Math.floor(Math.random() * playerTrackList.length);
    }

    const available = playerTrackList
      .map((_, i) => i)
      .filter(i => !playedIndices.has(i));

    if (available.length === 0) {
      setPlayedIndices(new Set());
      return Math.floor(Math.random() * playerTrackList.length);
    }

    return available[Math.floor(Math.random() * available.length)];
  };

  // --------------------------------
  // NEXT
  // --------------------------------
  const playNextTrack = () => {
    if (playerTrackList.length === 0 || currentTrackIndex === null) return;
  
    let nextIndex: number | null = null;
  
    if (isShuffleMode) {
      nextIndex = getRandomUnplayedIndex();
    } else {
      nextIndex = (currentTrackIndex + 1) % playerTrackList.length;
    }
  
    if (nextIndex === null) return;

    // Same track again (a one-song list, or shuffle starting over on it): nothing changes to
    // reload it, so restart it here.
    if (nextIndex === currentTrackIndex) {
      if (isShuffleMode) setPlayedIndices(prev => new Set([...prev, nextIndex!]));
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = 0;
        audio.play().catch(() => setIsPlaying(!audio.paused));
      }
      return;
    }

    setHistory(prev => [...prev, currentTrackIndex]);
    setCurrentTrackIndex(nextIndex);
  
    if (isShuffleMode) {
      setPlayedIndices(prev => new Set([...prev, nextIndex!]));
    }

    setIsPlaying(true);
  };
  playNextTrackRef.current = playNextTrack;

  // --------------------------------
  // PREVIOUS
  // --------------------------------
  const playPreviousTrack = () => {
    if (history.length === 0) return;

    const previousIndex = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));
    setCurrentTrackIndex(previousIndex);
    setIsPlaying(true);
  };

  // --------------------------------
  // TOGGLE
  // --------------------------------
  const togglePlayPause = () => {
    setIsPlaying(prev => !prev);
  };

  // --------------------------------
  // SEEK
  // --------------------------------
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const newTime = ratio * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // --------------------------------
  // SHUFFLE
  // --------------------------------
  const toggleShuffle = () => {
    setIsShuffleMode(prev => {
      const newValue = !prev;
  
      if (!newValue) {
        setPlayedIndices(new Set());
      } else {
        if (currentTrackIndex !== null) {
          setPlayedIndices(new Set([currentTrackIndex]));
        }
      }
  
      return newValue;
    });
  };

  const handleArtistClick = (artist: Artist) => {
    setModalArtist(artist);
    setIsModalArtistOpen(true);
  };

  const handleFullscreen = (_isFullscreen: boolean) => {
    // Right away, inside the click: browsers only allow fullscreen during a user gesture.
    if (_isFullscreen) enterBrowserFullscreen();
    const el = document.querySelector('.main-content');
    if (el) {
      el.classList.add('fade-out');
    
      const onAnimationEnd = () => {
        el.removeEventListener('animationend', onAnimationEnd);
        navigate('/');
      };    
      el.addEventListener('animationend', onAnimationEnd);
      setTimeout(() => {
        setIsFullscreen(_isFullscreen);
      }, 1000);
    } else {
      setIsFullscreen(_isFullscreen);
    }
  };

  const handleSeePlaylist = (url: string) => {
    navigate(url);
  };

  const hasTrack = !!currentTrack;
  // Not ready (loading icon instead of play) until the current track's link has arrived and loaded.
  const isPlayerReady = hasTrack && !!streamUrl && isAudioReady;
  const isInitialState = !hasTrack && playerTrackList.length > 0;
  const progressPercent = duration ? (currentTime / duration) * 100 : 0;

  // "Now playing": close with Escape, keep the page behind it from scrolling, and close it
  // if the track goes away.
  useEffect(() => {
    if (!isNowPlayingOpen) return;
    if (!hasTrack) { setIsNowPlayingOpen(false); return; }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsNowPlayingOpen(false); };
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [isNowPlayingOpen, hasTrack]);

  // Tap the progress bar to jump to that point.
  const seekToClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (controlsDisabled || !isPlayerReady || !audioRef.current || duration === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const newTime = ((e.clientX - rect.left) / rect.width) * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const playRandomTrack = () => {
    if (playerTrackList.length === 0) return;
    let randomIndex = Math.floor(Math.random() * playerTrackList.length);
    // Never "randomly" pick the track that is already playing.
    if (playerTrackList.length > 1 && randomIndex === currentTrackIndex) {
      randomIndex = (randomIndex + 1) % playerTrackList.length;
    }
    if (currentTrackIndex !== null) setHistory(prev => [...prev, currentTrackIndex]);
    setCurrentTrackIndex(randomIndex);
    setPlayedIndices(new Set([randomIndex]));
    setIsPlaying(true);
  };

  // "Play a random track" button: any track the user can play, like the mobile app.
  // If a selection or playlist is playing, go back to the full list first.
  const playAnyRandomTrack = () => {
    const all = allTracksRef.current;
    if (all.length === 0) return;
    if (playerTrackList === all) {
      playRandomTrack();
      return;
    }
    // Pick now and set it together with the list (never the song that is already playing).
    let randomIndex = Math.floor(Math.random() * all.length);
    if (all.length > 1 && all[randomIndex].id === currentTrack?.id) {
      randomIndex = (randomIndex + 1) % all.length;
    }
    pendingStartIndexRef.current = randomIndex;
    setPlaylist(null);
    setCurrentTrackIndex(randomIndex);
    setPlayerTrackList(all);
    setIsPlaying(true);
  };

  // "Play a random track" button, used in three places: floating (nothing playing yet),
  // in the desktop player's actions and in the mobile player's info row.
  const randomButton = (className: string, label = 'Random', hidden = false) => (
    <button
      type="button"
      className={className}
      // The 3D room ignores clicks on UI drawn over it (Room/Output.js isRoomEvent),
      // so this button does not also open the chest behind it.
      onClick={playAnyRandomTrack}
      disabled={playerTrackList.length === 0}
      aria-hidden={hidden}
      tabIndex={hidden ? -1 : undefined}
      aria-label="Play a random track"
      title="Play a random track"
    >
      {/* The record and tonearm from the Mattdigging logo (vinyl.svg), in the button's text colour. */}
      <svg width="17" height="16" viewBox="0 0 290 271" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M9.08398 51.725C14.781 64.8 27.817 73.942 42.991 73.942C60.192 73.942 74.648 62.195 78.778 46.286H101.173C67.759 68.023 45.663 105.693 45.663 148.526C45.663 215.841 100.232 270.41 167.547 270.41C234.862 270.41 289.431 215.841 289.431 148.526C289.431 81.212 234.862 26.642 167.547 26.642C159.178 26.642 151.006 27.486 143.111 29.093C137.704 28.112 131.659 27.656 124.439 27.656H78.778C74.649 11.747 60.192 0 42.991 0C27.818 0 14.782 9.14198 9.08398 22.217H5.409H0V27.626V46.317V51.726H5.409L9.08398 51.725ZM167.546 188.514C145.461 188.514 127.558 170.611 127.558 148.526C127.558 126.441 145.461 108.538 167.546 108.538C189.631 108.538 207.534 126.441 207.534 148.526C207.534 170.611 189.631 188.514 167.546 188.514ZM5.409 27.625H23.067C24.137 25.348 25.605 23.249 27.438 21.417C31.592 17.263 37.116 14.975 42.991 14.975C48.866 14.975 54.39 17.263 58.544 21.417C61.762 24.635 63.859 28.675 64.642 33.064H124.439C146.167 33.064 156.142 37.444 169.069 46.646L169.122 46.684L188.674 61.101L190.738 58.267L223.461 82.396L214.692 94.438L181.969 70.309L184.075 67.417L164.512 52.992C152.738 44.614 144.15 40.877 124.439 40.877H64.642C63.86 45.266 61.763 49.306 58.544 52.524C54.39 56.678 48.866 58.966 42.991 58.966C37.116 58.966 31.592 56.678 27.438 52.524C25.606 50.692 24.137 48.593 23.067 46.316H5.409V27.625Z" fill="currentColor" />
      </svg>
      <span>{label}</span>
    </button>
  );

  const handlePlayButtonClick = () => {
    if (isInitialState) {
      playRandomTrack();
    } else {
      togglePlayPause();
    }
  };

  return (
    <>
    {/* Nothing playing yet: the floating button. Once a track plays it fades out and
        the same action lives inside the player (see player__actions / player__mobile). */}
    {randomButton(`random-play ${hasTrack ? 'random-play--hidden' : ''}`, 'Play a random track', hasTrack)}

    {/* Hidden until a track is selected, then slides up from the bottom. */}
    <div
      className={`player ${isFullscreen ? 'player--fullscreen' : ''} ${hasTrack ? '' : 'player--hidden'}`}
      aria-hidden={!hasTrack}
    >
      {/* One permanent <audio>: only its source changes. (Re-creating it per track let a removed
          element keep playing off-page while the next track started — two songs at once.) */}
      <audio ref={audioRef} src={streamUrl ?? undefined} preload="auto" style={{ display: 'none' }} />

      {/* Desktop bar, YouTube Music style: a thin progress line along the top edge, then
          transport + time (left) · track + favourite (centre) · random, shuffle, fullscreen (right). */}
      <div className='player__bar'>
        <div className='player__controls__progress'>
          <span className='player__controls__progress__time'>{formatTime(currentTime)}</span>
          <div
            className={`player__controls__progress__bar${controlsDisabled || !isPlayerReady ? ' player__controls__progress__bar--disabled' : ''}`}
            onClick={(e) => {
              if (controlsDisabled || !isPlayerReady || !audioRef.current || duration === 0) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const ratio = (e.clientX - rect.left) / rect.width;
              const newTime = ratio * duration;
              audioRef.current.currentTime = newTime;
              setCurrentTime(newTime);
            }}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
            onMouseUp={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
            onMouseMove={(e) => { e.preventDefault(); e.stopPropagation(); if (isDragging) handleSeek(e); }}
          >
            <div className='player__controls__progress__track'>
              <div
                className='player__controls__progress__fill'
                style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
              />
              <div
                className='player__controls__progress__thumb'
                style={{ left: `${duration ? (currentTime / duration) * 100 : 0}%` }}
              />
            </div>
          </div>
          <span className='player__controls__progress__time'>{formatTime(duration)}</span>
        </div>
        <div className='player__left'>
          {/* Previous button */}
          <button
            onClick={playPreviousTrack}
            disabled={controlsDisabled || history.length === 0 || !isPlayerReady}
            className='player__btn player__btn--prev'
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 6V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M9.5 12L18 6V18L9.5 12Z" fill="currentColor"/>
            </svg>
          </button>

          {/* Play/Pause button */}
          <button
            onClick={handlePlayButtonClick}
            disabled={!isInitialState && (controlsDisabled || !isPlayerReady)}
            className={`player__btn player__btn--play${!isPlayerReady && !isInitialState ? ' player__btn--loading' : ''}`}
          >
            {!isPlayerReady && !isInitialState ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className='player__btn--loading'>
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeDasharray="40 20" strokeLinecap="round"/>
              </svg>
            ) : isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor"/>
                <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 5.14286V18.8571L19 12L8 5.14286Z" fill="currentColor"/>
              </svg>
            )}
          </button>

          {/* Next button */}
          <button
            onClick={playNextTrack}
            disabled={controlsDisabled || !isPlayerReady}
            className='player__btn player__btn--next'
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M14.5 12L6 18V6L14.5 12Z" fill="currentColor"/>
            </svg>
          </button>
          <span className='player__time'>{formatTime(currentTime)} / {formatTime(duration)}</span>
        </div>
        <div className='player__center'>
          <div className='player__info'>
            {hasTrack ? (
              <>
                <ImageStorage
                  path={currentTrack?.cover_url}
                  alt={currentTrack?.title}
                  bucket="covers"
                  className='player__info__cover'
                />
                <div className='player__info__text'>
                  <h3><MarqueeText text={currentTrack.title} /></h3>
                  <p className='player__info__artist-name' onClick={() => handleArtistClick(currentTrack?.artist)}><MarqueeText text={currentTrack?.artist?.name ?? ''} /></p>
                </div>
              </>
            ) : playerTrackList.length === 0 ? (
              <div className='player__info__skeleton'>
                <div className='player__info__skeleton__title' />
                <div className='player__info__skeleton__artist' />
              </div>
            ) : (
              <div className='player__info__empty' onClick={playRandomTrack}>
                <div className='player__info__empty__icon'>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                  </svg>
                </div>
                <div className='player__info__empty__text'>
                  <p>Click to play a random track</p>
                </div>
              </div>
            )}
          </div>
          {currentTrack && <FavoriteButton track={currentTrack} />}
          {hasTrack && playlist?.name && (
            <button type='button' className='player__info__playlist-name' onClick={() => handleSeePlaylist(playlist?.url)} title='Go to playlist'>
              <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' aria-hidden='true'><line x1='4' y1='6' x2='20' y2='6' /><line x1='4' y1='12' x2='20' y2='12' /><line x1='4' y1='18' x2='13' y2='18' /></svg>
              <span>{playlist?.name}</span>
            </button>
          )}
        </div>
        <div className='player__right'>
          {randomButton('player__random')}
          {/* Shuffle button */}
          <button
            onClick={toggleShuffle}
            disabled={controlsDisabled || !isPlayerReady}
            className={`player__btn player__btn--shuffle ${isShuffleMode ? 'player__btn--shuffle--active' : ''}`}
          >
            <svg width="27" height="24" viewBox="0 0 75 66" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M57.0552 1.15066C58.4927 -0.341536 60.8638 -0.388434 62.3599 1.0452L73.0119 11.2752C73.7502 11.9822 74.1642 12.9588 74.1642 13.9822C74.1642 15.0017 73.7501 15.9783 73.0119 16.6853L62.3599 26.9153C60.8638 28.3528 58.4927 28.302 57.0552 26.8098C55.6216 25.3176 55.6685 22.9426 57.1646 21.509L61.0982 17.7317H52.3716C49.7544 17.7317 47.27 18.9036 45.6099 20.927L35.7779 32.896L45.6959 44.919C47.3561 46.9346 49.8326 48.0987 52.4459 48.0987H61.1295L57.2076 44.3175C55.7154 42.88 55.6724 40.505 57.1099 39.0128C58.5474 37.5245 60.9224 37.4816 62.4146 38.9191L73.0196 49.1491C73.7501 49.8561 74.1641 50.8327 74.1641 51.8483C74.1641 52.8678 73.7501 53.8405 73.0196 54.5475L62.4146 64.7815C60.9224 66.219 58.5474 66.176 57.1099 64.6838C55.6724 63.1955 55.7154 60.8205 57.2076 59.383L61.1295 55.5978H52.4459C47.5943 55.5978 42.9967 53.4298 39.9109 49.6876L14.6059 19.0116C12.9457 16.996 10.4692 15.8319 7.85588 15.8319H3.75038C1.67618 15.8319 0.000380516 14.1522 0.000380516 12.0819C0.000380516 10.0077 1.67618 8.33192 3.75038 8.33192H7.85588C12.7075 8.33192 17.3051 10.4999 20.3909 14.2421L30.9139 27.0001L39.8123 16.1641C42.8982 12.4063 47.5076 10.2305 52.3713 10.2305H61.0979L57.1643 6.45322C55.6682 5.01962 55.6213 2.64462 57.0549 1.14852L57.0552 1.15066ZM20.9222 39.1667L25.7855 45.0612L20.4417 51.5651C17.3558 55.3229 12.7503 57.4987 7.8867 57.4987H3.75C1.6758 57.4987 0 55.8229 0 53.7487C0 51.6784 1.6758 49.9987 3.75 49.9987H7.8867C10.5039 49.9987 12.9844 48.8268 14.6484 46.8034L20.9222 39.1667Z" fill="var(--color-white)"/>
            </svg>
          </button>
          <div className='player__fullscreen' onClick={() => handleFullscreen(!isFullscreen)}>
            {isFullscreen ? (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.5 0.75C5.5 0.33579 5.16421 0 4.75 0C4.33579 0 4 0.33579 4 0.75V3.25C4 3.66421 3.66421 4 3.25 4H0.75C0.33579 4 0 4.33579 0 4.75C0 5.16421 0.33579 5.5 0.75 5.5H3.25C4.49264 5.5 5.5 4.49264 5.5 3.25V0.75ZM5.5 17.25C5.5 17.6642 5.16421 18 4.75 18C4.33579 18 4 17.6642 4 17.25V14.75C4 14.3358 3.66421 14 3.25 14H0.75C0.33579 14 0 13.6642 0 13.25C0 12.8358 0.33579 12.5 0.75 12.5H3.25C4.49264 12.5 5.5 13.5074 5.5 14.75V17.25ZM13.25 0C12.8358 0 12.5 0.33579 12.5 0.75V3.25C12.5 4.49264 13.5074 5.5 14.75 5.5H17.25C17.6642 5.5 18 5.16421 18 4.75C18 4.33579 17.6642 4 17.25 4H14.75C14.3358 4 14 3.66421 14 3.25V0.75C14 0.33579 13.6642 0 13.25 0ZM12.5 17.25C12.5 17.6642 12.8358 18 13.25 18C13.6642 18 14 17.6642 14 17.25V14.75C14 14.3358 14.3358 14 14.75 14H17.25C17.6642 14 18 13.6642 18 13.25C18 12.8358 17.6642 12.5 17.25 12.5H14.75C13.5074 12.5 12.5 13.5074 12.5 14.75V17.25Z" fill="var(--color-white)"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 2.75C1.5 2.05964 2.05964 1.5 2.75 1.5H4.75C5.16421 1.5 5.5 1.16421 5.5 0.75C5.5 0.33579 5.16421 0 4.75 0H2.75C1.23122 0 0 1.23122 0 2.75V4.75C0 5.16421 0.33579 5.5 0.75 5.5C1.16421 5.5 1.5 5.16421 1.5 4.75V2.75ZM1.5 15.25C1.5 15.9404 2.05964 16.5 2.75 16.5H4.75C5.16421 16.5 5.5 16.8358 5.5 17.25C5.5 17.6642 5.16421 18 4.75 18H2.75C1.23122 18 0 16.7688 0 15.25V13.25C0 12.8358 0.33579 12.5 0.75 12.5C1.16421 12.5 1.5 12.8358 1.5 13.25V15.25ZM15.25 1.5C15.9404 1.5 16.5 2.05964 16.5 2.75V4.75C16.5 5.16421 16.8358 5.5 17.25 5.5C17.6642 5.5 18 5.16421 18 4.75V2.75C18 1.23122 16.7688 0 15.25 0H13.25C12.8358 0 12.5 0.33579 12.5 0.75C12.5 1.16421 12.8358 1.5 13.25 1.5H15.25ZM16.5 15.25C16.5 15.9404 15.9404 16.5 15.25 16.5H13.25C12.8358 16.5 12.5 16.8358 12.5 17.25C12.5 17.6642 12.8358 18 13.25 18H15.25C16.7688 18 18 16.7688 18 15.25V13.25C18 12.8358 17.6642 12.5 17.25 12.5C16.8358 12.5 16.5 12.8358 16.5 13.25V15.25Z" fill="var(--color-white)"/>
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* Phones: mini player, like the app. Thin progress line, cover, title and artist,
          play and next. Tap it to open the full-screen "Now playing". */}
      <div className='player__mobile'>
        <div className='player__mini__progress' aria-hidden='true'>
          <div style={{ width: `${progressPercent}%` }} />
        </div>
        <div
          className='player__mini'
          role='button'
          tabIndex={0}
          aria-label='Open player'
          onClick={() => hasTrack && setIsNowPlayingOpen(true)}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && hasTrack) { e.preventDefault(); setIsNowPlayingOpen(true); } }}
        >
          <ImageStorage
            path={currentTrack?.cover_url}
            alt=''
            bucket="covers"
            className='player__mini__cover'
          />
          <div className='player__mini__text'>
            <span className='player__mini__title'><MarqueeText text={currentTrack?.title ?? ''} /></span>
            <span className='player__mini__artist'><MarqueeText text={currentTrack?.artist?.name ?? ''} /></span>
          </div>
          <button
            type='button'
            className='player__mini__btn'
            onClick={(e) => { e.stopPropagation(); handlePlayButtonClick(); }}
            disabled={!isInitialState && (controlsDisabled || !isPlayerReady)}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            <PlayPauseIcon size={26} isPlaying={isPlaying} loading={!isPlayerReady && !isInitialState} />
          </button>
          <button
            type='button'
            className='player__mini__btn'
            onClick={(e) => { e.stopPropagation(); playNextTrack(); }}
            disabled={controlsDisabled || !isPlayerReady}
            aria-label='Next track'
          >
            <NextIcon size={24} />
          </button>
        </div>
      </div>

      {/* Phones: full-screen "Now playing", like the app's. Rendered on <body>: the player bar
          is transformed, which would trap a fixed overlay inside it. */}
      {isNowPlayingOpen && currentTrack && createPortal(
        <div className='nowPlaying' role='dialog' aria-modal='true' aria-label='Now playing'>
          <div className='nowPlaying__header'>
            <button type='button' className='nowPlaying__icon' onClick={() => setIsNowPlayingOpen(false)} aria-label='Close player'>
              <svg width='26' height='26' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><polyline points='6 9 12 15 18 9' /></svg>
            </button>
            {playlist?.name ? (
              <button
                type='button'
                className='nowPlaying__playlist'
                onClick={() => { setIsNowPlayingOpen(false); handleSeePlaylist(playlist.url); }}
              >
                {playlist.name}
              </button>
            ) : <span />}
            {randomButton('nowPlaying__random')}
          </div>

          <ImageStorage
            path={currentTrack.cover_url}
            alt={currentTrack.title}
            bucket="covers"
            className='nowPlaying__cover'
          />

          <div className='nowPlaying__texts'>
            <div className='nowPlaying__titleRow'>
              <h2 className='nowPlaying__title'>{currentTrack.title}</h2>
              <FavoriteButton track={currentTrack} className='nowPlaying__favorite' />
            </div>
            {currentTrack.artist && (
              <button
                type='button'
                className='nowPlaying__artist'
                onClick={() => { setIsNowPlayingOpen(false); handleArtistClick(currentTrack.artist); }}
              >
                {currentTrack.artist.name}
              </button>
            )}
            {(currentTrack.album_name || currentTrack.year) && (
              <p className='nowPlaying__meta'>{[currentTrack.album_name, currentTrack.year].filter(Boolean).join(' · ')}</p>
            )}
          </div>

          <div className='nowPlaying__progress'>
            <div className='nowPlaying__progress__bar' onClick={seekToClick}>
              <div className='nowPlaying__progress__fill' style={{ width: `${progressPercent}%` }} />
            </div>
            <div className='nowPlaying__progress__times'>
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className='nowPlaying__controls'>
            {/* Random order on/off: neon with a dot when on, like the app. */}
            <button
              type='button'
              className={`nowPlaying__shuffle${isShuffleMode ? ' nowPlaying__shuffle--on' : ''}`}
              onClick={toggleShuffle}
              disabled={controlsDisabled || !isPlayerReady}
              role='switch'
              aria-checked={isShuffleMode}
              aria-label='Shuffle'
            >
              <svg width='24' height='21' viewBox='0 0 75 66' fill='currentColor' aria-hidden='true'>
                <path fillRule="evenodd" clipRule="evenodd" d="M57.0552 1.15066C58.4927 -0.341536 60.8638 -0.388434 62.3599 1.0452L73.0119 11.2752C73.7502 11.9822 74.1642 12.9588 74.1642 13.9822C74.1642 15.0017 73.7501 15.9783 73.0119 16.6853L62.3599 26.9153C60.8638 28.3528 58.4927 28.302 57.0552 26.8098C55.6216 25.3176 55.6685 22.9426 57.1646 21.509L61.0982 17.7317H52.3716C49.7544 17.7317 47.27 18.9036 45.6099 20.927L35.7779 32.896L45.6959 44.919C47.3561 46.9346 49.8326 48.0987 52.4459 48.0987H61.1295L57.2076 44.3175C55.7154 42.88 55.6724 40.505 57.1099 39.0128C58.5474 37.5245 60.9224 37.4816 62.4146 38.9191L73.0196 49.1491C73.7501 49.8561 74.1641 50.8327 74.1641 51.8483C74.1641 52.8678 73.7501 53.8405 73.0196 54.5475L62.4146 64.7815C60.9224 66.219 58.5474 66.176 57.1099 64.6838C55.6724 63.1955 55.7154 60.8205 57.2076 59.383L61.1295 55.5978H52.4459C47.5943 55.5978 42.9967 53.4298 39.9109 49.6876L14.6059 19.0116C12.9457 16.996 10.4692 15.8319 7.85588 15.8319H3.75038C1.67618 15.8319 0.000380516 14.1522 0.000380516 12.0819C0.000380516 10.0077 1.67618 8.33192 3.75038 8.33192H7.85588C12.7075 8.33192 17.3051 10.4999 20.3909 14.2421L30.9139 27.0001L39.8123 16.1641C42.8982 12.4063 47.5076 10.2305 52.3713 10.2305H61.0979L57.1643 6.45322C55.6682 5.01962 55.6213 2.64462 57.0549 1.14852L57.0552 1.15066ZM20.9222 39.1667L25.7855 45.0612L20.4417 51.5651C17.3558 55.3229 12.7503 57.4987 7.8867 57.4987H3.75C1.6758 57.4987 0 55.8229 0 53.7487C0 51.6784 1.6758 49.9987 3.75 49.9987H7.8867C10.5039 49.9987 12.9844 48.8268 14.6484 46.8034L20.9222 39.1667Z" />
              </svg>
              <span className='nowPlaying__shuffle__dot' />
            </button>
            <button
              type='button'
              className='nowPlaying__icon'
              onClick={playPreviousTrack}
              disabled={controlsDisabled || history.length === 0 || !isPlayerReady}
              aria-label='Previous track'
            >
              <PrevIcon size={34} />
            </button>
            <button
              type='button'
              className='nowPlaying__play'
              onClick={handlePlayButtonClick}
              disabled={controlsDisabled || !isPlayerReady}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              <PlayPauseIcon size={32} isPlaying={isPlaying} loading={!isPlayerReady} />
            </button>
            <button
              type='button'
              className='nowPlaying__icon'
              onClick={playNextTrack}
              disabled={controlsDisabled || !isPlayerReady}
              aria-label='Next track'
            >
              <NextIcon size={34} />
            </button>
            {/* Same width as shuffle, so play stays in the centre. */}
            <span className='nowPlaying__spacer' />
          </div>
        </div>,
        document.body,
      )}
    </div>
    </>
  );
};

export default Player;
